import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { schema } from "./schema";
import { searchCards } from "../api";
import type { CardSummary, Msg } from "../types";
import { renderCardPreview } from "./preview";
import { PREVIEW_LINES } from "../preview-limits";

const key = new PluginKey("cardLinkSuggest");

// Suggestion previews are standalone (not nested in another preview editor).
const SUGGEST_DEPTH = 1;
const noopDispatch: (msg: Msg) => void = () => {};

function getMatch(state: EditorState): { query: string; from: number } | null {
  const { $from } = state.selection;
  if ($from.parent.type.spec.code) return null;
  const textBefore = $from.parent.textBetween(0, $from.parentOffset);
  const match = /\[\[([^\n\]]*)$/.exec(textBefore);
  if (!match) return null;
  return { query: match[1]!, from: $from.pos - match[0].length };
}

class SuggestView {
  private el: HTMLUListElement;
  private items: CardSummary[] = [];
  private selected = 0;
  private match: { query: string; from: number } | null = null;
  private debounce: ReturnType<typeof setTimeout> | null = null;
  private lastQuery: string | null = null;
  private itemViews: EditorView[] = [];

  constructor(private view: EditorView) {
    this.el = document.createElement("ul");
    this.el.className = "suggest-dropdown";
    this.el.style.display = "none";
    document.body.appendChild(this.el);
  }

  update(view: EditorView) {
    this.view = view;
    const match = getMatch(view.state);

    if (!match) {
      this.hide();
      return;
    }

    this.match = match;

    if (match.query === this.lastQuery) {
      // Re-render at updated cursor position without refetching
      this.renderList();
      return;
    }

    this.lastQuery = match.query;
    if (this.debounce) clearTimeout(this.debounce);
    this.debounce = setTimeout(() => {
      searchCards(match.query || undefined, { orderByRelevance: true }).then((results) => {
        this.items = results.slice(0, 8);
        this.selected = 0;
        this.renderList();
      }).catch(() => this.hide());
    }, 150);
  }

  private renderList() {
    if (!this.match || this.items.length === 0) {
      this.hide();
      return;
    }

    const coords = this.view.coordsAtPos(this.view.state.selection.from);
    this.clearItemViews();
    this.el.innerHTML = "";
    this.items.forEach((item, i) => {
      const li = document.createElement("li");
      li.className = "suggest-dropdown__item" +
        (i === this.selected ? " suggest-dropdown__item--active" : "");
      const slot = document.createElement("div");
      li.appendChild(slot);
      this.itemViews.push(renderCardPreview(slot, item.content, SUGGEST_DEPTH, noopDispatch, PREVIEW_LINES.suggest));
      li.addEventListener("mousedown", (e) => {
        e.preventDefault();
        this.confirm(i);
      });
      this.el.appendChild(li);
    });

    this.el.style.display = "block";
    this.el.style.position = "fixed";
    this.el.style.left = `${coords.left}px`;
    this.el.style.top = `${coords.bottom + 4}px`;
    this.el.style.zIndex = "1000";
  }

  private clearItemViews() {
    this.itemViews.forEach((v) => v.destroy());
    this.itemViews = [];
  }

  hide() {
    this.el.style.display = "none";
    this.clearItemViews();
    this.items = [];
    this.match = null;
    this.lastQuery = null;
  }

  confirm(index: number) {
    const item = this.items[index];
    const match = this.match;
    if (!item || !match) return;

    const node = schema.nodes["card_link"]!.create({ uuid: item.id });
    this.view.dispatch(
      this.view.state.tr.replaceWith(match.from, this.view.state.selection.from, node)
    );
    this.view.focus();
    this.hide();
  }

  handleKeyDown(_view: EditorView, event: KeyboardEvent): boolean {
    if (this.el.style.display === "none" || this.items.length === 0) return false;

    if (event.key === "ArrowDown") {
      this.selected = (this.selected + 1) % this.items.length;
      this.renderList();
      return true;
    }
    if (event.key === "ArrowUp") {
      this.selected = (this.selected - 1 + this.items.length) % this.items.length;
      this.renderList();
      return true;
    }
    if (event.key === "Enter") {
      this.confirm(this.selected);
      return true;
    }
    if (event.key === "Escape") {
      this.hide();
      return true;
    }
    return false;
  }

  destroy() {
    if (this.debounce) clearTimeout(this.debounce);
    this.clearItemViews();
    this.el.remove();
  }
}

export function cardLinkSuggestPlugin(): Plugin {
  let suggestView: SuggestView | null = null;

  return new Plugin({
    key,
    view(editorView) {
      suggestView = new SuggestView(editorView);
      return suggestView;
    },
    props: {
      handleKeyDown(view, event) {
        return suggestView?.handleKeyDown(view, event) ?? false;
      },
    },
  });
}
